import { useState } from "react";
import { ChevronDown, CircleHelp, TriangleAlert } from "lucide-react";
import {
  PsvReviewItemKind,
  PsvReviewOutcome,
  PsvReviewStatus,
} from "@api/shared-runtime/psv-review";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { CheckLimitations, SourceDetails } from "../source-result-row";
import { CheckRecoveryNotice } from "../check-recovery-notice";
import { StateExclusionsDetails } from "../state-exclusions-group";
import {
  STATE_MEDICAID_EXCLUSIONS_SOURCE_ID,
  type ReportItem,
  type RunReport,
  type RunScope,
} from "../types";
import { ItemActions } from "./item-actions";
import {
  ItemStaffFiles,
  ItemUploadButton,
  NO_EVIDENCE_NUDGE,
} from "./item-evidence-upload";
import { ItemNote } from "./item-note";
import {
  checkDetailLimitations,
  decisionAttribution,
  findDecidingResult,
  findItemCheck,
  isOpenItem,
  isRetryWaiting,
  showsIssueCallout,
} from "./item-status";
import {
  issueSummary,
  earlierConcernNote,
} from "../../provider-screenings/issues/issue-labels";
import { IssueDetails } from "../../license-verification/license-result";
import type { LicenseIssue } from "../../license-verification/license-issues";
import { getResultIssues } from "../../license-verification/result-issues";
import { LicenseSources, LicenseSourceSummary } from "./license-item-details";
import { MedicareOptOutRecords } from "../registry-check-results/medicare-opt-out-records";
import { StopRetryingButton } from "./stop-retrying";
import type { ItemDecisions } from "./use-item-decisions";

const NOT_AVAILABLE_CALLOUT = "border-yellow-200 bg-yellow-50 text-yellow-900";
const ISSUES_CALLOUT = "border-amber-200 bg-amber-50 text-amber-900";

/** The Medicare result an item covers, whose opt-out records the banner
 * lists; null for other items. */
function medicareFacts(check: RunReport["checks"][number] | undefined) {
  if (check?.family !== "medicare-participation") {
    return null;
  }
  return check.results?.[0]?.facts ?? null;
}

/** The banner's issue summary. For a Medicare item it also opens the opt-out
 * records it counts, closed by default so a known, unchanged record does not
 * fill the item. */
function IssueSummary({
  item,
  check,
}: {
  item: ReportItem;
  check: RunReport["checks"][number] | undefined;
}) {
  const summary = issueSummary(item.issues);
  const concernNote = earlierConcernNote(item.issues);
  const facts = medicareFacts(check);
  const text = [summary, concernNote].filter(Boolean).join(" · ");
  if (!facts) {
    return text ? (
      <p className="text-xs leading-5 font-medium">{text}</p>
    ) : null;
  }
  return (
    <Collapsible className="space-y-2">
      <CollapsibleTrigger asChild>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="group h-auto gap-1 p-0 text-left text-xs leading-5 font-medium text-inherit"
        >
          {text || "Opt-out records reported by CMS"}
          <ChevronDown
            className="size-3 shrink-0 transition-transform group-data-[state=open]:rotate-180"
            aria-hidden
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="rounded-md bg-background p-3 text-foreground">
        <MedicareOptOutRecords facts={facts} issues={item.issues} />
      </CollapsibleContent>
    </Collapsible>
  );
}

/** A single issue is the banner's heading, so only its details follow. Several
 * are listed under the verdict; none leaves the verdict's own description. */
function IssueCalloutBody({
  description,
  issues,
}: {
  description: string;
  issues: LicenseIssue[];
}) {
  if (issues.length === 0) {
    return <p className="text-xs leading-5">{description}</p>;
  }
  if (issues.length === 1) {
    return <IssueDetails details={issues[0].details} />;
  }
  return (
    <ul className="space-y-1.5">
      {issues.map((issue, index) => (
        <li key={`${issue.title}-${index}`} className="space-y-0.5">
          <p className="text-xs font-medium leading-5">{issue.title}</p>
          <IssueDetails details={issue.details} />
        </li>
      ))}
    </ul>
  );
}

function IssueCallout({
  item,
  check,
  issues,
}: {
  item: ReportItem;
  check: RunReport["checks"][number] | undefined;
  issues: LicenseIssue[];
}) {
  if (!showsIssueCallout(item)) {
    return null;
  }
  // The state Medicaid group already says which states were not screened or
  // limited, and what to do, so a banner that only lists those names again is
  // noise. With issues the banner stays: it carries their summary.
  if (
    check?.sourceId === STATE_MEDICAID_EXCLUSIONS_SOURCE_ID &&
    item.issues.length === 0
  ) {
    return null;
  }
  const unavailable = item.outcome === PsvReviewOutcome.NotAvailable;
  const Icon = unavailable ? CircleHelp : TriangleAlert;
  const onlyIssue = issues.length === 1 ? issues[0] : undefined;
  return (
    <section
      aria-label="Issue"
      className={cn(
        "space-y-1 rounded-lg border p-3 text-sm",
        unavailable ? NOT_AVAILABLE_CALLOUT : ISSUES_CALLOUT,
      )}
    >
      <p className="flex items-center gap-1.5 font-semibold">
        <Icon className="size-4 shrink-0" aria-hidden />
        {onlyIssue ? onlyIssue.title : item.verdict.label}
      </p>
      <IssueCalloutBody
        description={item.verdict.description}
        issues={issues}
      />
      <IssueSummary item={item} check={check} />
    </section>
  );
}

function isDecided(status: PsvReviewStatus) {
  return (
    status === PsvReviewStatus.Accepted || status === PsvReviewStatus.Concern
  );
}

/** Who decided and when, with the note, and a way to change the decision. */
function DecisionAttribution({
  decision,
  title,
  canUndo,
  busy,
  onUndo,
}: {
  decision: NonNullable<ReportItem["decision"]>;
  title: string;
  canUndo: boolean;
  busy: boolean;
  onUndo: () => void;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1 text-xs text-muted-foreground">
      <div className="min-w-0 space-y-0.5">
        <p>{decisionAttribution(decision)}</p>
        {decision.note && <p className="break-words">Note: {decision.note}</p>}
      </div>
      {canUndo && (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs"
          disabled={busy}
          aria-label={`Undo ${title}`}
          onClick={onUndo}
        >
          Change decision
        </Button>
      )}
    </div>
  );
}

function CheckItemSources({
  item,
  checks,
  runId,
  scope,
}: {
  item: ReportItem;
  checks: RunReport["checks"];
  runId: string;
  scope: RunScope;
}) {
  const check = findItemCheck({ item, checks });
  if (!check) {
    return null;
  }
  // A source that did not finish says how to check it by hand.
  const recovery = check.recovery && (
    <CheckRecoveryNotice recovery={check.recovery} />
  );
  if (check.sourceId === STATE_MEDICAID_EXCLUSIONS_SOURCE_ID) {
    return (
      <>
        {recovery}
        <StateExclusionsDetails
          check={check}
          runId={runId}
          scope={scope}
          issues={item.issues}
        />
      </>
    );
  }
  return (
    <>
      {recovery}
      <CheckLimitations
        check={check}
        detailLimitations={checkDetailLimitations(check)}
      />
      <SourceDetails
        check={check}
        runId={runId}
        scope={scope}
        resultKey={item.resultKey ?? undefined}
        issues={item.issues}
      />
    </>
  );
}

export function ItemDetails({
  id,
  item,
  title,
  checks,
  runId,
  scope,
  canEdit,
  decisions,
  uploadFocusRequested,
  onUploadFocused,
  onAccept,
  onConcern,
  onUndo,
}: {
  id: string;
  item: ReportItem;
  title: string;
  checks: RunReport["checks"];
  runId: string;
  scope: RunScope;
  canEdit: boolean;
  decisions: ItemDecisions;
  uploadFocusRequested: boolean;
  onUploadFocused: () => void;
  onAccept: () => void;
  onConcern: () => void;
  onUndo: () => void;
}) {
  const decision = item.decision;
  const editable = canEdit && isOpenItem(item);
  const bannerShowsIssues = showsIssueCallout(item);
  // A license's banner states its deciding source's issues once, instead of
  // repeating them inside the source.
  const decided = bannerShowsIssues
    ? findDecidingResult({ item, checks })
    : null;
  const bannerIssues = decided
    ? getResultIssues(decided.check, decided.result)
    : [];
  const retryWaitingChecks = checks.filter(
    (check) => item.checkIds.includes(check.id) && isRetryWaiting(check),
  );
  return (
    <div id={id} className="space-y-3 border-t bg-slate-50/50 p-3 sm:p-4">
      <IssueCallout
        item={item}
        check={findItemCheck({ item, checks })}
        issues={bannerIssues}
      />
      {decision && isDecided(decision.status) && (
        <DecisionAttribution
          decision={decision}
          title={title}
          canUndo={canEdit}
          busy={decisions.isBusy(item.key)}
          onUndo={onUndo}
        />
      )}
      {canEdit &&
        retryWaitingChecks.map((check) => (
          <StopRetryingButton
            key={check.id}
            check={check}
            runId={runId}
            scope={scope}
          />
        ))}
      {item.kind === PsvReviewItemKind.StateLicense && item.license && (
        <>
          <LicenseSourceSummary license={item.license} />
          <LicenseSources
            license={item.license}
            checks={checks}
            runId={runId}
            scope={scope}
            issuesShownAbove={bannerShowsIssues}
          />
        </>
      )}
      {item.kind === PsvReviewItemKind.Check && (
        <CheckItemSources
          item={item}
          checks={checks}
          runId={runId}
          scope={scope}
        />
      )}
      {decision && (
        <ItemStaffFiles
          item={item}
          runId={runId}
          scope={scope}
          canEdit={canEdit}
          decisions={decisions}
        />
      )}
      {editable && !item.hasEvidence && (
        <div
          role="status"
          className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed p-3 text-xs leading-5 text-muted-foreground"
        >
          <p className="min-w-0 flex-1">{NO_EVIDENCE_NUDGE}</p>
          <ItemUploadButton
            item={item}
            title={title}
            decisions={decisions}
            focusRequested={uploadFocusRequested}
            onFocused={onUploadFocused}
          />
        </div>
      )}
      {editable && decision && (
        <EditableFooter
          // A saved note from elsewhere replaces the local draft.
          key={decision.note}
          item={item}
          title={title}
          decisions={decisions}
          onAccept={onAccept}
          onConcern={onConcern}
        />
      )}
    </div>
  );
}

function EditableFooter({
  item,
  title,
  decisions,
  onAccept,
  onConcern,
}: {
  item: ReportItem;
  title: string;
  decisions: ItemDecisions;
  onAccept: () => void;
  onConcern: () => void;
}) {
  const savedNote = item.decision?.note ?? "";
  const [draft, setDraft] = useState(savedNote);
  const trimmed = draft.trim();
  return (
    <>
      <ItemNote
        title={title}
        draft={draft}
        onDraftChange={setDraft}
        defaultOpen={savedNote !== "" || (item.decision?.files.length ?? 0) > 0}
      >
        {item.hasEvidence && (
          <ItemUploadButton
            item={item}
            title={title}
            decisions={decisions}
            focusRequested={false}
            onFocused={() => undefined}
            label="Attach file"
          />
        )}
      </ItemNote>
      <ItemActions
        title={title}
        busy={decisions.isBusy(item.key)}
        hasUnsavedNote={trimmed !== "" && trimmed !== savedNote.trim()}
        onAccept={onAccept}
        onConcern={onConcern}
        onSaveNote={() => void decisions.saveNote({ item, note: trimmed })}
      />
    </>
  );
}
